(() => {
  const COLOR_NAMES = ['color', 'colour', '颜色', '顏色'];

  const normalize = (value) => String(value || '')
    .trim()
    .toLocaleLowerCase()
    .replace(/[\s_-]+/g, ' ');

  const explicitGroup = (alt) => {
    const match = String(alt || '').trim().match(/^(?:color|colour|颜色|顏色)\s*[:：|]\s*(.+)$/i);
    return match ? normalize(match[1]) : null;
  };

  class VariantMediaGroups {
    constructor(gallery) {
      this.gallery = gallery;
      this.sectionId = gallery.id.replace(/^MediaGallery-/, '').replace(/-duplicate$/, '');
      this.viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      this.thumbnails = gallery.querySelector('[id^="GalleryThumbnails-"]');
      this.optionNames = this.readOptionNames();
      this.colorIndex = this.findColorIndex();
      this.colorValues = this.readColorValues();
      this.prepareMedia();
      this.bindNavigation();
      this.apply(this.selectedColor(), false);
      gallery.dataset.vmgReady = '';
    }

    readOptionNames() {
      try {
        return JSON.parse(this.gallery.dataset.vmgOptionNames || '[]');
      } catch (error) {
        return [];
      }
    }

    picker() {
      return document.querySelector(`variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`);
    }

    findColorIndex() {
      const namedIndex = this.optionNames.findIndex((name) => COLOR_NAMES.includes(normalize(name)));
      if (namedIndex >= 0) return namedIndex;

      const picker = this.picker();
      if (!picker) return -1;
      const fields = Array.from(picker.querySelectorAll('.product-form__input'));
      const groupedAlts = Array.from(this.gallery.querySelectorAll('[data-media-alt]'))
        .map((item) => explicitGroup(item.dataset.mediaAlt))
        .filter(Boolean);
      return fields.findIndex((field) => {
        const values = Array.from(field.querySelectorAll('input, option')).map((item) => normalize(item.value));
        return values.some((value) => groupedAlts.includes(value));
      });
    }

    readColorValues() {
      const picker = this.picker();
      if (!picker || this.colorIndex < 0) return [];
      const field = picker.querySelectorAll('.product-form__input')[this.colorIndex];
      if (!field) return [];
      return Array.from(field.querySelectorAll('input, option')).map((item) => normalize(item.value)).filter(Boolean);
    }

    mediaGroup(item) {
      const alt = item.dataset.mediaAlt || '';
      const explicit = explicitGroup(alt);
      if (explicit) return explicit;
      const exact = normalize(alt);
      return this.colorValues.includes(exact) ? exact : '';
    }

    prepareMedia() {
      this.gallery.querySelectorAll('[data-media-id], [data-target]').forEach((item) => {
        item.dataset.vmgGroup = this.mediaGroup(item);
      });
    }

    selectedColor() {
      const picker = this.picker();
      if (!picker || this.colorIndex < 0) return '';
      const field = picker.querySelectorAll('.product-form__input')[this.colorIndex];
      if (!field) return '';
      const selected = field.querySelector('input:checked, select');
      return normalize(selected && selected.value);
    }

    apply(color, activateFirst = true) {
      const selected = normalize(color);
      const slides = Array.from(this.viewer.querySelectorAll('[data-media-id]'));
      slides.forEach((item) => this.toggleItem(item, selected));
      if (this.thumbnails) {
        this.thumbnails.querySelectorAll('[data-target]').forEach((item) => this.toggleItem(item, selected));
      }

      const visible = this.visibleSlides();
      const active = visible.find((item) => item.classList.contains('is-active'));
      if (visible.length && (activateFirst || !active)) this.gallery.setActiveMedia(visible[0].dataset.mediaId, false);

      if (this.viewer.resetPages) this.viewer.resetPages();
      if (this.thumbnails && this.thumbnails.resetPages) this.thumbnails.resetPages();
      this.updateDesktopButtons();
    }

    toggleItem(item, selected) {
      const group = item.dataset.vmgGroup;
      const hidden = Boolean(group && selected && group !== selected);
      item.toggleAttribute('data-vmg-hidden', hidden);
      item.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    }

    visibleSlides() {
      return Array.from(this.viewer.querySelectorAll('[data-media-id]:not([data-vmg-hidden])'));
    }

    bindNavigation() {
      this.prev = this.gallery.querySelector('.vmg-navigation-button--prev');
      this.next = this.gallery.querySelector('.vmg-navigation-button--next');
      if (this.prev) this.prev.addEventListener('click', () => this.move(-1));
      if (this.next) this.next.addEventListener('click', () => this.move(1));
      if (this.thumbnails) {
        this.thumbnails.addEventListener('click', () => window.setTimeout(() => this.updateDesktopButtons()));
      }
    }

    move(direction) {
      const visible = this.visibleSlides();
      if (!visible.length) return;
      let index = visible.findIndex((item) => item.classList.contains('is-active'));
      if (index < 0) index = 0;
      const nextIndex = Math.max(0, Math.min(visible.length - 1, index + direction));
      this.gallery.setActiveMedia(visible[nextIndex].dataset.mediaId, false);
      this.updateDesktopButtons();
    }

    updateDesktopButtons() {
      const visible = this.visibleSlides();
      const index = visible.findIndex((item) => item.classList.contains('is-active'));
      if (this.prev) this.prev.disabled = visible.length < 2 || index <= 0;
      if (this.next) this.next.disabled = visible.length < 2 || index < 0 || index >= visible.length - 1;
    }
  }

  const initialize = () => {
    document.querySelectorAll('media-gallery[data-vmg-option-names]').forEach((gallery) => {
      if (!gallery.dataset.vmgInitialized) {
        gallery.dataset.vmgInitialized = 'true';
        gallery._variantMediaGroups = new VariantMediaGroups(gallery);
      }
    });
  };

  document.addEventListener('DOMContentLoaded', initialize);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('change', (event) => {
    const picker = event.target.closest('variant-radios, variant-selects');
    if (!picker) return;
    const sectionId = picker.dataset.section;
    window.setTimeout(() => {
      document.querySelectorAll(`[id^="MediaGallery-${sectionId}"]`).forEach((gallery) => {
        if (gallery._variantMediaGroups) gallery._variantMediaGroups.apply(gallery._variantMediaGroups.selectedColor());
      });
    });
  });

})();
